/**
 * Roles & Access — route `/staff/roles`. Shared StaffPage chrome (full-bleed
 * black header, ruby underline, counts) with ruby pill tabs (Roles / Who has
 * what / Access log). Replaces the old path-list StaffRolesPage.
 */
import { useEffect, useState } from 'react';
import { type RolePermissions } from '@platform/shared-types';
import { staffApi, type StaffMember, type StaffRole } from '../../../services/staffApi';
import { toastService } from '../../../services/toastService';
import { useStaffAccess } from '../../../hooks/useStaffAccess';
import { ShieldCheck } from 'lucide-react';
import { RubyLoader } from '../../../components/RubyLoader';
import { StaffPage, StaffSubtabs } from '../../../components/staff/StaffChrome';
import { RoleList } from './RoleList';
import { RoleEditor } from './RoleEditor';
import { WhoHasWhat } from './WhoHasWhat';
import { AccessLog } from './AccessLog';

type Tab = 'roles' | 'who' | 'log';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'roles', label: 'Roles' },
  { id: 'who', label: 'Who has what' },
  { id: 'log', label: 'Access log' },
];

export function RolesAccessPage() {
  const { isOwner, levels, me } = useStaffAccess();
  const [tab, setTab] = useState<Tab>('roles');
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null | 'new'>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [roleRes, staffRes] = await Promise.all([staffApi.roles.list(), staffApi.staff.list()]);
      setRoles(roleRes.roles || []);
      setStaff((staffRes.staff || []).filter((s: any) => !s.isOwner));
    } catch (e: any) {
      setError(e?.message || 'Could not load Roles & Access');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Default selection once roles arrive: the caller's own role if it exists, else the first.
  useEffect(() => {
    if (selectedId !== null || roles.length === 0) return;
    setSelectedId(roles[0].id);
  }, [roles, selectedId]);

  const selectedRole: StaffRole | null = selectedId === 'new' || selectedId === null
    ? null
    : roles.find((r) => r.id === selectedId) ?? null;

  const isMyRole = !!selectedRole && me?.staff?.roleId === selectedRole.id;

  const handleSaved = (saved: StaffRole) => {
    setRoles((prev) => {
      const exists = prev.some((r) => r.id === saved.id);
      return exists ? prev.map((r) => (r.id === saved.id ? saved : r)) : [...prev, saved];
    });
    setSelectedId(saved.id);
    toastService.success(selectedId === 'new' ? 'Role created' : 'Role saved');
    // staffCount / members can shift after an edit — refresh staff quietly.
    staffApi.staff.list().then((r) => setStaff((r.staff || []).filter((s: any) => !s.isOwner))).catch(() => {});
  };

  const handleDeleted = (roleId: string) => {
    setRoles((prev) => prev.filter((r) => r.id !== roleId));
    setSelectedId(null);
    toastService.success('Role deleted');
  };

  // The Master Owner is hidden from every list (server-side); never counted.
  const superAdmins = staff.filter((s) => s.status !== 'terminated' && !s.isOwner).length;

  const myLevels: RolePermissions = levels;

  return (
    <StaffPage
      icon={ShieldCheck}
      title="Roles & Access"
      subtitle="Decide which side-menu sections each role can see and change. Enforced on every page and every API call."
      actions={
        <div className="flex items-center gap-6 mr-1">
          {[
            { n: roles.length, label: 'Roles' },
            { n: superAdmins, label: 'Super admins' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-xl font-bold text-white">{s.n}</div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
      }
      tabs={<StaffSubtabs tabs={TABS} active={tab} onChange={(id) => setTab(id as Tab)} />}
    >
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <RubyLoader variant="section" />
      ) : tab === 'roles' ? (
        <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
          <RoleList
            roles={roles}
            selectedId={selectedId === 'new' ? null : selectedId}
            onSelect={(r) => setSelectedId(r.id)}
            onNew={() => setSelectedId('new')}
          />
          {(selectedRole || selectedId === 'new') && (
            <RoleEditor
              role={selectedId === 'new' ? null : selectedRole}
              myLevels={myLevels}
              isOwner={isOwner}
              isMyRole={isMyRole}
              staff={staff}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
            />
          )}
        </div>
      ) : tab === 'who' ? (
        <WhoHasWhat staff={staff} roles={roles} />
      ) : (
        <AccessLog />
      )}
    </div>
    </StaffPage>
  );
}

export default RolesAccessPage;
